import Vue from 'vue'
import VueRouter from 'vue-router'

import ErrorPage from '../views/ErrorPage.vue'

import store from '../store/index'
import { apiService } from '../service/api.service'
import jwtService from '../service/jwt.service'
Vue.use(VueRouter)

const ifAuthenticated = async (to, from, next) => {
  const token = jwtService.getToken()
  if (!token) {
    store.dispatch('setAuthenticated', false)
    next({ name: 'adminLogin' })
    return
  }
  const res: any = await apiService.get('confirm-login/').catch(() => {
    store.dispatch('setAuthenticated', false)
    return false
  })
  if (res) {
    store.dispatch('setAuthenticated', true)
    store.commit('setUser', res.data.user)
  }
  if (store.getters.isAuthenticated) {
    next()
  } else {
    next({ name: 'adminLogin' })
  }
}

const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push (location) {
  return originalPush.call(this, location).catch((err) => err)
}

const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace (location) {
  return originalReplace.call(this, location).catch((err) => err)
}

export default new VueRouter({
  routes: [
    {
      path: '/login',
      name: 'adminLogin',
      component: () => import('../views/AdminLogin.vue')
    },
    {
      path: '/',
      redirect: { name: 'dashboard' }
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/Dashboard.vue')
    },

    {
      path: '/error',
      name: 'ErrorPage',
      component: ErrorPage
    },
    {
      path: '/users',
      name: 'users',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/Users.vue')
    },
    {
      path: '/licences',
      name: 'licences',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/Licences.vue')
    },
    {
      path: '/devices',
      name: 'devices',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/Devices.vue')
    },
    {
      path: '/dockers',
      name: 'dockers',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/Dockers.vue')
    },
    {
      path: '/container-tags',
      name: 'containerTags',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/ContainerTags.vue')
    },
    {
      path: '/clients',
      name: 'clients',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/Clients.vue')
    },
    {
      path: '/services',
      name: 'services',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/CordatusServices.vue')
    },
    {
      path: '/env-profile',
      name: 'envProfile',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/EnvProfile.vue')
    },
    {
      path: '/version-management',
      name: 'versionManagement',
      redirect: '/version-management/client-version',
      beforeEnter: ifAuthenticated,
      component: () =>
        import('../views/VersionManagementPages/VersionManagement.vue'),
      children: [
        {
          path: 'client-version',
          name: 'clientVersion',
          component: () =>
            import('../views/VersionManagementPages/ClientVersion.vue')
        },
        {
          path: 'cossa-version',
          name: 'cossaVersion',
          component: () =>
            import('../views/VersionManagementPages/CossaVersion.vue')
        },
        {
          path: 'onvif-discovery-version',
          name: 'onvifDiscoveryVersion',
          component: () =>
            import('../views/VersionManagementPages/OnvifDiscoveryVersion.vue')
        },
        {
          path: 'stream-engine-version',
          name: 'streamVersion',
          component: () =>
            import('../views/VersionManagementPages/StreamEngineVersion.vue')
        },
        {
          path: 'inference-engine-version',
          name: 'inferenceEngineVersion',
          component: () =>
            import(
              '../views/VersionManagementPages/InferenceEngineVersion.vue'
            )
        }
      ]
    },
    {
      path: '/general',
      name: 'generalSettings',
      beforeEnter: ifAuthenticated,
      redirect: '/general/token-settings',
      component: () => import('../views/settings_cordatus/GeneralSettings.vue'),
      children: [
        {
          path: 'pricing-content',
          name: 'pricingContentSettings',
          component: () => import('../views/settings_cordatus/PricingContentSettings.vue')
        },
        {
          path: 'token-settings',
          name: 'tokenSettings',
          component: () =>
            import('../views/settings_cordatus/TokenSettings.vue')
        },
        {
          path: 'parameter-settings',
          name: 'parameterSettings',
          component: () =>
            import('../views/settings_cordatus/ParameterSettings.vue')
        },
        {
          path: 'email-settings',
          name: 'emailSettings',
          component: () =>
            import('../views/settings_cordatus/EmailSettings/EmailSettings.vue')
        },
        {
          path: 'key-settings',
          name: 'keySettings',
          component: () =>
            import('../views/settings_cordatus/KeySettings.vue')
        },
        {
          path: 'app-automation-settings',
          name: 'appAutomationSettings',
          component: () =>
            import('../views/settings_cordatus/AppAutomationSettings.vue')
        }
      ]
    },
    {
      path: '/engines',
      name: 'engines',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/Engines.vue')
    },
    {
      path: '/sparkrun-recipes',
      name: 'sparkrunRecipes',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/SparkrunRecipes.vue')
    },
    {
      path: '/container-options',
      name: 'containerOptions',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/ContainerOptions.vue')
    },
    {
      path: '/llm-engines',
      name: 'llm-engines',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/Models.vue')
    },
    {
      path: '/vram-calculator',
      name: 'vram-calculator',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/VRamCalculator.vue')
    },
    {
      path: '/jetson-compatibility',
      name: 'jetson-compatibility',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/JetsonCompatibility.vue')
    },
    {
      path: '/logs',
      name: 'logs',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/Logs.vue')
    },
    {
      path: '/email-campaigns',
      name: 'email-campaigns',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/email-campaigns/Campaigns.vue')
    },
    {
      path: '/gift-backfill',
      name: 'gift-backfill',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/GiftBackfill.vue')
    },
    {
      path: '/email-campaigns/templates/new',
      name: 'email-template-new',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/email-campaigns/TemplateBuilder.vue')
    },
    {
      path: '/email-campaigns/templates/:id',
      name: 'email-template-edit',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/email-campaigns/TemplateBuilder.vue')
    },
    {
      path: '/user-journey',
      name: 'user-journey',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/UserJourney.vue')
    },
    {
      path: '/lifecycle-reports',
      name: 'lifecycle-reports',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/LifecycleReports.vue')
    },
    {
      path: '/resellers',
      name: 'resellers',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/Resellers.vue')
    },
    {
      path: '/partner-applications',
      name: 'partner-applications',
      beforeEnter: ifAuthenticated,
      component: () => import('../views/PartnerApplications.vue')
    }
  ]
})
